import { Chart } from "chart.js";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNewDashboardDetails } from "../../reduxStore/action/dashboardAdmin";
import {
  FaCalendar,
  FaTimes,
  FaRedoAlt,
  FaCalendarCheck,
  FaUserCheck,
  FaUser,
  FaLandmark,
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  DoughnutController,
  ArcElement,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Card from "./Card";
import strings from "translate/language";
import { Link, useNavigate } from "react-router-dom";

const Dashboard1 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formVaild, setFormVaild] = useState({});
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const chartInstanceRef = useRef(null);

  const todayDate = new Date().toISOString().split("T")[0];
  const oneMonthAgoObj = new Date();
  oneMonthAgoObj.setDate(1);
  const oneMonthAgo = oneMonthAgoObj.toISOString().split("T")[0];
  const [form, setForm] = useState({
    start_date: oneMonthAgo,
    end_date: todayDate,
  });

  const { dashboardDetails } = useSelector(
    (state) => state.dashboardDetailAdmin,
  );

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getNewDashboardDetails({
        start_date: form.start_date,
        end_date: form.end_date,
      }),
    ).finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (dashboardDetails?.data) {
      setDashboardData(dashboardDetails.data); // Set real backend data
      setIsLoading(false);
    }
  }, [dashboardDetails]);

  const filterDetail = () => {
    if (!handleValid()) {
      setForm({ ...form });
      dispatch(
        getNewDashboardDetails({
          start_date: form.start_date,
          end_date: form.end_date,
        }),
      );
    }
  };

  const filterReset = () => {
    setForm({
      // isFiltered: false,
      start_date: oneMonthAgo,
      end_date: todayDate,
    });
    setFormVaild({
      start_date: "",
      end_date: "",
    });
    dispatch(
      getNewDashboardDetails({
        start_date: form.start_date,
        end_date: form.end_date,
      }),
    );
  };

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;
    if (!form.start_date) {
      formError = true;
      err["start_date"] = strings.startDateErr;
    }
    if (!form.end_date) {
      formError = true;
      err["end_date"] = strings.endDateErr;
    }
    setFormVaild(err);
    return formError;
  };

  const handleInputField = (e) => {
    const nextFormState = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(nextFormState);
    if (e.target.name === "start_date") {
      setFormVaild({
        ...formVaild,
        // start_date: "",
      });
    } else if (e.target.name === "end_date") {
      setFormVaild({
        ...formVaild,
        // end_date: "",
      });
    }
  };

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    DoughnutController,
    ArcElement,
  );

  // Setup chart when the component mounts
  useEffect(() => {
    if (
      dashboardData &&
      dashboardData.customers &&
      dashboardData.customers.other_sources
    ) {
      const ctx = document.getElementById("myPieChart");

      // Destroy the previous chart instance if it exists
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      // Extract keys and values for the Doughnut chart
      const labels = Object.keys(dashboardData.customers.other_sources); // Source titles
      const data = Object.values(dashboardData.customers.other_sources); // Counts for each source

      // Generate background colors dynamically
      const backgroundColors = labels.map((_, index) => {
        return `hsl(${(index * 360) / labels.length}, 70%, 50%)`;
      });

      // Create a new chart instance
      chartInstanceRef.current = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: labels, // Source titles as labels
          datasets: [
            {
              data: data, // Count of customers for each source
              backgroundColor: backgroundColors,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              position: "bottom", // Places the labels below the chart
              labels: {
                boxWidth: 20, // Size of the colored box for each label
                padding: 20, // Adds space between the label text and the chart
              },
            },
          },
          layout: {
            padding: {
              top: 20, // Adds padding at the top of the chart
              bottom: 20, // Adds padding between the chart and the labels
            },
          },
          responsive: true,
          maintainAspectRatio: false, // Ensures the chart adapts well to different screen sizes
        },
      });
    }

    // Cleanup on component unmount
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [dashboardData]);

  const truncateName = (name) => {
    return name?.length > 55 ? name.slice(0, 62) + "..." : name;
  };

  // Styles for the outer and inner progress bars
  const outerBarStyle = {
    width: "100%", // Full width of the parent container
    height: "30px", // Height of the bar
    backgroundColor: "#e0e0e0", // Background color of the outer bar
    borderRadius: "15px", // Rounded edges
    marginBottom: "20px", // Spacing between progress bars
    position: "relative", // For absolute positioning of the inner bar
  };

  const innerBarStyle = (progress, color) => ({
    position: "absolute", // Position relative to the outer bar
    left: "5px", // Margin from the left
    right: "5px",
    height: "70%", // Full height of the outer bar
    top: "15%",
    width: `${progress}%`, // Dynamic width based on progress
    backgroundColor: color, // Color of the inner bar
    borderRadius: "10px", // Rounded edges for inner bar
    marginBottom: "5px",
    transition: "width 0.3s ease", // Smooth transition for the width
  });

  const handleRevisitCompleteCardClick = () => {
    navigate(
      `/appointment?start_date=${form.start_date}&end_date=${form.end_date}&Revisit_Completed`,
      { state: { hiddenStatus: "12" } },
    );
  };

  const handleBookingCancelledCardClick = () => {
    navigate(
      `/booking_list?start_date=${form.start_date}&end_date=${form.end_date}&Booking_Cancelled`,
      { state: { hiddenStatus: "4" } },
    );
  };

  const propertyBookings = dashboardData?.bookings?.property_bookings || [];

  return (
    <div className="page-body">
      <div className="container-fluid">
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12 col-xl-12">
              <div className="row">
                <div className="col-sm-12">
                  <div className="card">
                    <div className="card-body">
                      <form className="theme-form">
                        <div className="row">
                          <div className="col-md-4">
                            <span className="cardText">
                              {strings.startDate}
                            </span>
                            <div
                              className="input-group date"
                              id="dt-date"
                              data-target-input="nearest"
                            >
                              <input
                                className="form-control datetimepicker-input digits"
                                type="date"
                                data-target="#dt-date"
                                name="start_date"
                                value={form.start_date}
                                onChange={handleInputField}
                                // min={form.start_date}
                              />
                            </div>
                            {formVaild.start_date && (
                              <span style={{ color: "red" }}>
                                {formVaild.start_date}
                              </span>
                            )}
                          </div>
                          <div className="col-md-4">
                            <span className="cardText">{strings.endDate}</span>
                            <div
                              className="input-group date"
                              id="dt-date"
                              data-target-input="nearest"
                            >
                              <input
                                className="form-control datetimepicker-input digits"
                                type="date"
                                name="end_date"
                                value={form.end_date}
                                onChange={handleInputField}
                                min={form.start_date}
                                // max={form.end_date}
                              />
                            </div>
                            {formVaild.end_date && (
                              <span style={{ color: "red" }}>
                                {formVaild.end_date}
                              </span>
                            )}
                          </div>
                          <div className="col-md-4 pt-5">
                            <div className="form-group d-flex justify-content-end">
                              <button
                                className="btn btn-warning mr-4"
                                type="button"
                                onClick={filterDetail}
                              >
                                {strings.search}
                              </button>
                              <button
                                className="btn btn-dark"
                                type="button"
                                onClick={filterReset}
                              >
                                {strings.reset}
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <div className="page-header-left">
                <h3>{strings.dashboard}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        {/* Header Cards Section */}
        <div className="row g-4">
          {[
            {
              title: "Total Visitors",
              icon: <FaUser size={22} />,
              total: dashboardData?.customers?.total_customers || 0,
              metrics: [
                {
                  label: "By CP",
                  value:
                    dashboardData?.customers?.customers_by_source?.[
                      "Channel Partner"
                    ] || 0,
                },
                {
                  label: "Direct",
                  value:
                    dashboardData?.customers?.customers_by_source?.Direct || 0,
                },
              ],
              totalKey: dashboardData?.customers?.total_customers || 1,
              redirectPath: "/visitor",
            },
            {
              title: "Appointments",
              icon: <FaCalendar size={22} />,
              total: dashboardData?.appointments?.total_appointments || 0,
              metrics: [
                {
                  label: "Actual Visits",
                  value: dashboardData?.appointments?.total_actual_visits || 0,
                },
                {
                  label: "Scheduled",
                  value:
                    dashboardData?.appointments?.total_appointments_scheduled ||
                    0,
                },
              ],
              totalKey: dashboardData?.appointments?.total_appointments || 1,
              redirectPath: "/appointment",
            },
            {
              title: "Total Bookings",
              icon: <FaCalendarCheck size={22} />,
              total: dashboardData?.bookings?.total_bookings_count || 0,
              metrics: [
                {
                  label: "Via CP",
                  value:
                    dashboardData?.bookings?.bookings_by_source?.[
                      "Channel Partner"
                    ] || 0,
                },
                {
                  label: "Direct",
                  value:
                    dashboardData?.bookings?.bookings_by_source?.Direct || 0,
                },
              ],
              totalKey: dashboardData?.bookings?.total_bookings_count || 1,
              redirectPath: "/booking_list",
            },
          ].map((card, index) => (
            <div key={index} className="col-xl-4 col-md-6 pt-3">
              <Link
                to={{
                  pathname: card.redirectPath,
                  search: `?start_date=${form.start_date}&end_date=${form.end_date}`,
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div className="card border-0 rounded-4 shadow overflow-hidden h-100">
                  <div className="p-3 d-flex align-items-center justify-content-between bg-light-subtle">
                    <div>
                      <h6 className="text-uppercase text-muted mb-1">
                        {card.title}
                      </h6>
                      <h3 className="mb-0 fw-bold">{card.total}</h3>
                    </div>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 48, height: 48, background: "#f1f3f5" }}
                    >
                      {card.icon}
                    </div>
                  </div>
                  <div className="p-3 bg-white border-top">
                    <div className="row">
                      {card.metrics.map((metric, i) => {
                        const percent = (metric.value / card.totalKey) * 100;
                        return (
                          <div key={i} className="col-6">
                            <small className="text-muted">{metric.label}</small>
                            <div className="fw-semibold">{metric.value}</div>
                            <div
                              className="progress mt-1"
                              style={{ height: "4px" }}
                            >
                              <div
                                className="progress-bar bg-success"
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="row mt-5">
          <Card
            title="Revisits Completed"
            value={dashboardData?.appointments?.total_revisits}
            icon={<FaRedoAlt color="#b0b3b8" />}
            onClick={handleRevisitCompleteCardClick}
            borderColor="primary"
          />
          <Card
            title="Booking Cancelled"
            value={dashboardData?.bookings?.total_canceled_bookings_count}
            icon={<FaTimes color="#d45555" />}
            onClick={handleBookingCancelledCardClick}
            borderColor="success"
          />
          <Card
            title="Active Cps"
            value={dashboardData?.channePartners?.total_active_cps}
            icon={<FaUserCheck color="#57785c" />}
            borderColor="info"
            subText="Last 60 days"
          />
          <Card
            title="Active Property"
            value={dashboardData?.channePartners?.total_active_property}
            icon={<FaLandmark color="#728FCE" />}
            borderColor="warning"
          />
        </div>

        {/* Channel Partners Section */}
        <div className="row mt-4">
          <div className="col-xl-7 col-lg-7 d-flex flex-column">
            <div className="card shadow mb-4 flex-fill">
              <div className="card-header py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Top Ten Channel Partners (in terms of visitors created)
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  {dashboardData?.channePartners?.top_ten_cps?.length > 0 ? (
                    dashboardData.channePartners.top_ten_cps.map(
                      (partner, index) => (
                        <div className="col-md-12" key={partner.cp_id}>
                          <div className="d-flex justify-content-between align-items-center border-bottom py-2">
                            <div className="d-flex align-items-center">
                              <span className="font-weight-bold mr-3">
                                {index + 1}.
                              </span>
                              <span className="text-muted">
                                {truncateName(partner.name)}
                              </span>
                            </div>
                            <span className="font-weight-bold">
                              ({partner.lead_count})
                            </span>
                          </div>
                        </div>
                      ),
                    )
                  ) : (
                    <div className="col-12 text-center text-muted">
                      No data available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Visitors by Other Sources Chart */}
          <div className="col-xl-5 col-lg-5 d-flex flex-column">
            <div className="card shadow mb-4 flex-fill">
              <div className="card-header py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Visitors by Other Sources
                </h6>
              </div>
              <div className="card-body">
                {dashboardData?.customers?.other_sources ? (
                  <>
                    <div
                      className="chart-pie pb-2"
                      style={{ height: "500px", margin: "0 auto" }}
                    >
                      <canvas id="myPieChart"></canvas>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-secondary">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Projects and Properties Section */}
        <div className="row mt-4">
          {/* Booking Conversion */}
          <div className="col-lg-6 mb-4 d-flex flex-column">
            <div className="card shadow mb-4 flex-fill">
              <div className="card-header py-3">
                <h6 className="m-0 font-weight-bold text-primary">
                  Booking Conversion of Properties{" "}
                  <span className="text-info">(in terms of lead)</span>
                </h6>
                <p className="m-0 text-secondary">
                  Overall Booking Conversion:
                  <span className="fw-bold text-dark">
                    {" "}
                    {dashboardData?.bookings?.booking_conversion}%
                  </span>
                </p>
              </div>
              <div
                className="card-body overflow-auto"
                style={{ maxHeight: "400px" }}
              >
                {dashboardData?.bookings?.property_conversions?.map(
                  (property, index) => (
                    <div key={index}>
                      <h4 className="small font-weight-bold">
                        {property.property_title || `Property ${index + 1}`}
                        <span className="float-right">
                          {property.conversionRate}%
                        </span>
                      </h4>
                      <div style={outerBarStyle}>
                        <div
                          style={innerBarStyle(
                            property.conversionRate,
                            "#28a745",
                          )}
                        ></div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>

          {/* Top Properties */}
          <div className="col-lg-6 mb-4 d-flex flex-column">
            <div className="card shadow mb-4 flex-fill">
              <div className="card-header py-3">
                <h6 className="m-0 font-weight-bold text-primary">
                  Top Properties
                </h6>
                <p className="card-text text-muted">(By booking count)</p>
              </div>
              <div className="card-body pt-0">
                <div
                  className="table-responsive"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <table className="table">
                    <thead
                      className="thead-light"
                      style={{ position: "sticky", top: 0, zIndex: 1 }}
                    >
                      <tr>
                        <th className="text-primary">Sr.No</th>
                        <th className="text-primary">Property Name</th>
                        <th className="text-primary">Bookings</th>
                        <th className="text-primary">Conversion%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.bookings?.property_bookings
                        ?.sort((a, b) => b.totalBookings - a.totalBookings)
                        .map((page, index) => (
                          <tr key={index}>
                            <td>
                              <strong>{index + 1}.</strong>
                            </td>
                            <td>
                              <strong>{page.property_title}</strong>
                            </td>
                            <td>
                              <strong>{page.totalBookings}</strong>
                            </td>
                            <td>
                              <span className="text-muted">
                                ({page.conversionFlatRate}%)
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Selling Section */}
        <div className="row mt-4">
          {/* Top selling closing manger */}
          <div className="col-lg-6 mb-4">
            <div
              className="card shadow-lg rounded-lg"
              style={{ backgroundColor: "#e4e4e4ff", border: "none" }}
            >
              <div
                className="card-header text-center py-3"
                style={{
                  backgroundColor: "#979797ff",
                  color: "black",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
              >
                <h5 className="font-weight-bold mb-0">
                  Top Selling Closing Managers
                </h5>
              </div>

              <div
                className="card-body p-4"
                style={{ height: "350px", overflowY: "auto" }}
              >
                {/* Table Header */}
                <div
                  className="d-flex justify-content-between align-items-center mb-3 text-dark text-bold"
                  style={{ borderBottom: "2px solid #6b6b6bff" }}
                >
                  <div
                    className="font-weight-semibold"
                    style={{ width: "15%" }}
                  >
                    Sr. No
                  </div>
                  <div
                    className="font-weight-semibold"
                    style={{ width: "60%" }}
                  >
                    Closing Manager
                  </div>
                  <div
                    className="font-weight-semibold text-right"
                    style={{ width: "25%" }}
                  >
                    Sellings
                  </div>
                </div>

                {/* Table Content */}
                <div>
                  {dashboardData?.topSellings?.topSelleingClosingManagers?.map(
                    (topSeller, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center mb-3 p-3"
                        style={{
                          backgroundColor: "#c5c4c4ff",
                          borderRadius: "8px",
                          cursor: "pointer",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.transform = "scale(1.02)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.transform = "scale(1)")
                        }
                      >
                        {/* Sr. No */}
                        <div style={{ width: "15%" }} className="text-dark">
                          {index + 1}
                        </div>

                        {/* Closing Manager Name */}
                        <div style={{ width: "60%" }} className="text-dark">
                          {topSeller.managerName || `Manager ${index + 1}`}
                        </div>

                        {/* Total Bookings (Sellings) */}
                        <div
                          style={{ width: "25%" }}
                          className="text-dark text-right"
                        >
                          {topSeller.totalBookings}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Top selling sourcing manger */}
          <div className="col-lg-6 mb-4">
            <div
              className="card shadow-lg rounded-lg"
              style={{ backgroundColor: "#ccccccff", border: "none" }}
            >
              <div
                className="card-header text-center py-3"
                style={{
                  backgroundColor: "#777777ff",
                  color: "white",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
              >
                <h5 className="font-weight-bold mb-0">
                  Top Selling Sourcing Managers
                </h5>
              </div>

              <div
                className="card-body p-4"
                style={{ height: "350px", overflowY: "auto" }}
              >
                {/* Table Header */}
                <div
                  className="d-flex justify-content-between align-items-center mb-3 text-dark"
                  style={{ borderBottom: "2px solid #444444" }}
                >
                  <div
                    className="font-weight-semibold"
                    style={{ width: "15%" }}
                  >
                    Sr. No
                  </div>
                  <div
                    className="font-weight-semibold"
                    style={{ width: "60%" }}
                  >
                    Sourcing Manager
                  </div>
                  <div
                    className="font-weight-semibold text-right"
                    style={{ width: "25%" }}
                  >
                    Sellings
                  </div>
                </div>

                {/* Table Content */}
                <div>
                  {dashboardData?.topSellings?.topSelleingSourcingManagers?.map(
                    (topSeller, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center mb-3 p-3"
                        style={{
                          backgroundColor: "#707070ff",
                          borderRadius: "8px",
                          cursor: "pointer",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.transform = "scale(1.02)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.transform = "scale(1)")
                        }
                      >
                        {/* Sr. No */}
                        <div style={{ width: "15%" }} className="text-white">
                          {index + 1}
                        </div>

                        {/* Closing Manager Name */}
                        <div style={{ width: "60%" }} className="text-white">
                          {topSeller.managerName || `Manager ${index + 1}`}
                        </div>

                        {/* Total Bookings (Sellings) */}
                        <div
                          style={{ width: "25%" }}
                          className="text-white text-right"
                        >
                          {topSeller.totalBookings}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-4">
          {/* Top Selling Closing Manager */}
          <div className="col-lg-6 mb-4">
            <div
              className="card shadow-lg rounded-lg"
              style={{ backgroundColor: "#F0F8FF", border: "none" }} // Light blue background
            >
              <div
                className="card-header text-center py-3"
                style={{
                  backgroundColor: "#4682B4", // Deep blue header
                  color: "white", // White text on the header
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
              >
                <h5 className="font-weight-bold mb-0">
                  Top Selling Closing Managers
                </h5>
              </div>

              <div
                className="card-body p-4"
                style={{ height: "350px", overflowY: "auto" }}
              >
                {/* Table Header */}
                <div
                  className="d-flex justify-content-between align-items-center mb-3 text-dark text-bold"
                  style={{ borderBottom: "2px solid #4682B4" }} // Blue border for the header
                >
                  <div
                    className="font-weight-semibold"
                    style={{ width: "15%" }}
                  >
                    Sr. No
                  </div>
                  <div
                    className="font-weight-semibold"
                    style={{ width: "60%" }}
                  >
                    Closing Manager
                  </div>
                  <div
                    className="font-weight-semibold text-right"
                    style={{ width: "25%" }}
                  >
                    Sellings
                  </div>
                </div>

                {/* Table Content */}
                <div>
                  {dashboardData?.topSellings?.topSelleingClosingManagers?.map(
                    (topSeller, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center mb-3 p-3"
                        style={{
                          backgroundColor: "#B0C4DE", // Light steel blue for table row
                          borderRadius: "8px",
                          cursor: "pointer",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.transform = "scale(1.02)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.transform = "scale(1)")
                        }
                      >
                        <div style={{ width: "15%" }} className="text-dark">
                          {index + 1}
                        </div>
                        <div style={{ width: "60%" }} className="text-dark">
                          {topSeller.managerName || `Manager ${index + 1}`}
                        </div>
                        <div
                          style={{ width: "25%" }}
                          className="text-dark text-right"
                        >
                          {topSeller.totalBookings}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Top Selling Sourcing Manager */}
          <div className="col-lg-6 mb-4">
            <div
              className="card shadow-lg rounded-lg"
              style={{ backgroundColor: "#F5FFFA", border: "none" }} // Mint cream background
            >
              <div
                className="card-header text-center py-3"
                style={{
                  backgroundColor: "#32CD32", // Lime green header
                  color: "white", // White text on the header
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
              >
                <h5 className="font-weight-bold mb-0">
                  Top Selling Sourcing Managers
                </h5>
              </div>

              <div
                className="card-body p-4"
                style={{ height: "350px", overflowY: "auto" }}
              >
                {/* Table Header */}
                <div
                  className="d-flex justify-content-between align-items-center mb-3 text-dark text-bold"
                  style={{ borderBottom: "2px solid #9fc29fff" }} // Green border for the header
                >
                  <div
                    className="font-weight-semibold"
                    style={{ width: "15%" }}
                  >
                    Sr. No
                  </div>
                  <div
                    className="font-weight-semibold"
                    style={{ width: "60%" }}
                  >
                    Sourcing Manager
                  </div>
                  <div
                    className="font-weight-semibold text-right"
                    style={{ width: "25%" }}
                  >
                    Sellings
                  </div>
                </div>

                {/* Table Content */}
                <div>
                  {dashboardData?.topSellings?.topSelleingSourcingManagers?.map(
                    (topSeller, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center mb-3 p-3"
                        style={{
                          backgroundColor: "#d8fad8ff", // Pale green for table row
                          borderRadius: "8px",
                          cursor: "pointer",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.transform = "scale(1.02)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.transform = "scale(1)")
                        }
                      >
                        <div style={{ width: "15%" }} className="text-dark">
                          {index + 1}
                        </div>
                        <div style={{ width: "60%" }} className="text-dark">
                          {topSeller.managerName || `Manager ${index + 1}`}
                        </div>
                        <div
                          style={{ width: "25%" }}
                          className="text-dark text-right"
                        >
                          {topSeller.totalBookings}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard1;
